<template>
  <div v-if="booking.status !== BookingStatus.CANCELED" class="space-y-4">
    <CVButton variant="danger" :is-loading="isSaving" @click.prevent="cancelBooking">
      {{ t('cancelBooking') }}
    </CVButton>
  </div>

  <div v-else-if="!hideRevertCancelling">
    <CVButton :is-loading="isSaving" @click.prevent="revertCancelling">
      {{ t('revertCancelling') }}
    </CVButton>
  </div>
</template>

<script lang="ts" setup>
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { Alert } from '@/services/alert.service';
import { DateTime } from 'luxon';
import CVButton from '@/components/buttons/CVButton.vue';
import { CancellationFee, CancelledBy } from '@carvia/ros-client-types';
import { useUpdateBooking } from '@/queries/use-bookings.ts';

const CANCELLATION_FEES: Record<string, number> = {
  [CancellationFee.NO_FEE]: 0,
  [CancellationFee.WITHIN_SEVEN_DAYS]: 0.3,
  [CancellationFee.AFTER_BOOKING_START_DATE]: 1.0,
};

const props = defineProps<{
  booking: Booking;
  hideRevertCancelling?: boolean;
}>();

const emit = defineEmits<{
  (e: 'bookingCancelled'): void;
}>();

const { t } = useI18n();

const { isPending: isSaving, mutateAsync: updateBooking } = useUpdateBooking();

const cancelBooking = async () => {
  const {
    reason,
    cancellationFee,
    autoCancellationFee,
    isConfirmed: isFeeConfirmed,
  } = await showFeeAlert();
  if (!isFeeConfirmed) return;
  const { cancelledBy, isConfirmed: isCauserConfirmed } = await showCancelledByAlert(reason);
  if (!isCauserConfirmed) return;
  const { comment, isConfirmed: isCommentConfirmed } = await showFeeCommentAlert(reason);
  if (!isCommentConfirmed) return;
  if (autoCancellationFee === CancellationFee.NO_FEE) {
    const { isConfirmed } = await Alert.fire({
      text: t('reallyCancelBookingText'),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: t('cancelBooking'),
      denyButtonText: t('dontCancel'),
      focusDeny: true,
    });
    if (!isConfirmed) return;
  }

  const { sendMail } = await showSendMailAlert();
  await updateBooking({
    id: props.booking.id,
    booking: {
      status: BookingStatus.CANCELED,
    },
    query: {
      overrideCancellationFee: cancellationFee,
      overrideCancellationFeeComment: reason
        ? t('reason.' + reason) + (comment ? ' | ' + comment : '')
        : undefined,
      cancelledBy,
      sendCancelledMail: sendMail,
    },
  });
  await Alert.fire({
    titleText: t('bookingCancelledTitle'),
    text: t('bookingCancelledText'),
    icon: 'success',
    confirmButtonText: t('ok'),
  });
  emit('bookingCancelled');
};

const showFeeAlert = async () => {
  const startDate = DateTime.fromISO(props.booking.startDate);
  const now = DateTime.now();
  const startDateDiff = startDate.diff(now, 'days').days;
  const autoCancellationFee =
    startDateDiff < 0
      ? CancellationFee.AFTER_BOOKING_START_DATE
      : startDateDiff < 7
        ? CancellationFee.WITHIN_SEVEN_DAYS
        : CancellationFee.NO_FEE;
  const autoCancellationFeeInPercent = Math.round(CANCELLATION_FEES[autoCancellationFee] * 100);
  if (
    !(
      props.booking.status === BookingStatus.CONFIRMED ||
      props.booking.status === BookingStatus.FLOATING ||
      props.booking.status === BookingStatus.HANDED_OVER
    ) ||
    autoCancellationFee === CancellationFee.NO_FEE
  ) {
    return {
      reason: Reason.NO_REASON,
      cancellationFee: CancellationFee.NO_FEE,
      autoCancellationFee,
      isConfirmed: true,
    };
  }
  const { value: reason, isConfirmed } = await Alert.fire<Reason>({
    text: t('cancellationFeeText', { feeInPercent: autoCancellationFeeInPercent }),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('ok'),
    denyButtonText: t('dontCancel'),
    input: 'select',
    inputOptions: {
      [Reason.NO_REASON]: t('noReason'),
      [Reason.MISAPPROPRIATION]: getSelectOptionText(
        Reason.MISAPPROPRIATION,
        CancellationFee.NO_FEE,
      ),
      [Reason.NOT_AVAILABLE]: getSelectOptionText(Reason.NOT_AVAILABLE, CancellationFee.NO_FEE),
      [Reason.GOODWILL]: getSelectOptionText(
        Reason.GOODWILL,
        autoCancellationFee === CancellationFee.AFTER_BOOKING_START_DATE
          ? CancellationFee.WITHIN_SEVEN_DAYS
          : CancellationFee.NO_FEE,
      ),
    },
  });
  const cancellationFee = determineCancellationFee(startDateDiff, reason);
  return { reason, cancellationFee, autoCancellationFee, isConfirmed };
};

const getSelectOptionText = (reason: Reason, cancellationFee: CancellationFee) => {
  return t('optionText', {
    reason: t('reason.' + reason),
    fee: Math.round(CANCELLATION_FEES[cancellationFee] * 100),
  });
};

enum Reason {
  NO_REASON = '',
  MISAPPROPRIATION = 'misappropriation',
  NOT_AVAILABLE = 'notAvailable',
  GOODWILL = 'goodwill',
}

const determineCancellationFee = (startDateDiff: number, reason: Reason | undefined) => {
  if (reason === Reason.MISAPPROPRIATION || reason === Reason.NOT_AVAILABLE)
    return CancellationFee.NO_FEE;
  if (!reason && startDateDiff < 0) return CancellationFee.AFTER_BOOKING_START_DATE;
  if (reason && startDateDiff < 0) return CancellationFee.WITHIN_SEVEN_DAYS;
  if (!reason && startDateDiff < 7) return CancellationFee.WITHIN_SEVEN_DAYS;
  return CancellationFee.NO_FEE;
};

const showCancelledByAlert = async (reason: Reason | undefined) => {
  if (reason && reason !== Reason.GOODWILL) return { cancelledBy: undefined, isConfirmed: true };
  const { value: cancelledBy, isConfirmed } = await Alert.fire<CancelledBy>({
    text: t('cancellationCauserText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('ok'),
    denyButtonText: t('dontCancel'),
    input: 'select',
    inputOptions: {
      [CancelledBy.US]: t('cancelledBy.' + CancelledBy.US),
      [CancelledBy.CUSTOMER]: t('cancelledBy.' + CancelledBy.CUSTOMER),
    },
  });
  return { cancelledBy, isConfirmed };
};

const showSendMailAlert = async () => {
  const { isConfirmed: sendMail } = await Alert.fire({
    text: t('sendMailText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('yes'),
    denyButtonText: t('no'),
  });
  return { sendMail };
};

const showFeeCommentAlert = async (reason: Reason | undefined) => {
  if (!reason) return { comment: undefined, isConfirmed: true };
  const { value: comment, isConfirmed } = await Alert.fire<string>({
    text: t('feeComment', { reason: t('reason.' + reason) }),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('ok'),
    denyButtonText: t('dontCancel'),
    input: 'textarea',
    inputPlaceholder: t('feeCommentPlaceholder'),
    inputValidator: (value: string) => value.length < 10 && t('commentTooShort'),
  });
  return { comment, isConfirmed };
};

const revertCancelling = async () => {
  const { isConfirmed, value } = await Alert.fire<boolean | undefined>({
    text: t('reallyRevertCancellationText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('revertCancellation'),
    denyButtonText: t('dontRevert'),
    focusDeny: true,
    input: 'checkbox',
    inputLabel: t('cancelCancellationFeeInvoice'),
    inputValue: true,
  });
  if (!isConfirmed) return;

  await updateBooking({
    id: props.booking.id,
    booking: {
      status: BookingStatus.DRAFT,
    },
    query: { cancelCancellationFeeInvoice: value === true },
  });
  await Alert.fire({
    titleText: t('cancellationRevertedTitle'),
    text: t('cancellationRevertedText'),
    icon: 'success',
    confirmButtonText: t('ok'),
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "cancelBooking": "Cancel Booking",
    "reallyCancelBookingText": "Do you really want to cancel the Booking?",
    "bookingCancelledTitle": "Booking cancelled",
    "bookingCancelledText": "The Booking was successfully cancelled",
    "dontCancel": "Don't cancel",
    "cancellationFeeText": "After our cancellation rules, cancellation fees of {feeInPercent}% of the total price are due. If we should charge less or no cancellation fee, please choose the reason here",
    "noReason": "No reason - charge cancellation fee",
    "reason": {
      "misappropriation": "Customer could misappropriate car",
      "notAvailable": "Booked car is not available",
      "goodwill": "Goodwill"
    },
    "optionText": "{reason} - to {fee}% fee",
    "cancellationCauserText": "Cancelled by ...",
    "cancelledBy": {
      "US": "Cancelled by us",
      "CUSTOMER": "Cancelled by Customer"
    },
    "feeComment": "More infos about selection: \"{reason}\"",
    "feeCommentPlaceholder": "More reasons and details on the reduction of the fee",
    "commentTooShort": "Comment too short",
    "ok": "OK",
    "revertCancelling": "Revert cancelling",
    "reallyRevertCancellationText": "Do you really want to revert the cancellation?",
    "revertCancellation": "Revert cancellation",
    "dontRevert": "Don't revert",
    "cancelCancellationFeeInvoice": "Cancel the cancellation fee invoice, if one exists",
    "cancellationRevertedTitle": "Cancellation reverted",
    "cancellationRevertedText": "The cancellation was successfully reverted",
    "sendMailText": "Do you want to send a cancellation mail to the customer?"
  },
  "de": {
    "cancelBooking": "Buchung stornieren",
    "reallyCancelBookingText": "Möchtest du die Buchung wirklich stornieren?",
    "bookingCancelledTitle": "Buchung storniert",
    "bookingCancelledText": "Die Buchung wurde erfolgreich storniert",
    "dontCancel": "Abbrechen",
    "cancellationFeeText": "Nach unseren Stornierungsregeln werden Gebühren von {feeInPercent}% des Gesamtpreises für die Stornierung fällig. Falls wir weniger/keine Gebühren in Rechnung stellen sollen, gib hier bitte den Grund an",
    "noReason": "Kein Grund - Gebühr berechnen",
    "reason": {
      "misappropriation": "Kunde könnte Auto unterschlagen",
      "notAvailable": "Gebuchtes Auto ist nicht verfügbar",
      "goodwill": "Kulanz"
    },
    "optionText": "{reason} - auf {fee}% Gebühr",
    "cancellationCauserText": "Storniert durch ...",
    "cancelledBy": {
      "US": "Storniert durch uns",
      "CUSTOMER": "Storniert durch Kunde"
    },
    "feeComment": "Mehr Infos zur Auswahl: \"{reason}\"",
    "feeCommentPlaceholder": "Weitere Gründe und Details zur Herabsetzung der Gebühr",
    "commentTooShort": "Kommentar zu kurz",
    "ok": "OK",
    "revertCancelling": "Stornierung rückgängig machen",
    "reallyRevertCancellationText": "Möchtest du die Stornierung wirklich rückgängig machen?",
    "revertCancellation": "Stornierung rückgängig machen",
    "dontRevert": "Nicht rückgängig machen",
    "cancelCancellationFeeInvoice": "Stornogebühren-Rechnung stornieren, falls vorhanden",
    "cancellationRevertedTitle": "Stornierung rückgängig gemacht",
    "cancellationRevertedText": "Die Stornierung wurde erfolgreich rückgängig gemacht",
    "sendMailText": "Möchten Sie eine Stornierungsmail an den Kunden senden?"
  },
  "es": {
    "cancelBooking": "Cancelar reserva",
    "reallyCancelBookingText": "¿Seguro que desea cancelar la reserva?",
    "bookingCancelledTitle": "Reserva cancelada",
    "bookingCancelledText": "La reserva se ha cancelado correctamente",
    "dontCancel": "No cancelar",
    "cancellationFeeText": "Según nuestras condiciones de cancelación, corresponde una tasa del {feeInPercent} % del precio total. Si debe cobrarse menos o ninguna tasa, elija aquí el motivo",
    "noReason": "Sin motivo: cobrar tasa de cancelación",
    "reason": {
      "misappropriation": "Riesgo de apropiación indebida del vehículo",
      "notAvailable": "El vehículo reservado no está disponible",
      "goodwill": "Cortesía"
    },
    "optionText": "{reason} — tasa al {fee} %",
    "cancellationCauserText": "Cancelado por…",
    "cancelledBy": {
      "US": "Cancelado por nosotros",
      "CUSTOMER": "Cancelado por el cliente"
    },
    "feeComment": "Más información sobre la opción «{reason}»",
    "feeCommentPlaceholder": "Más motivos y detalles sobre la reducción de la tasa",
    "commentTooShort": "Comentario demasiado corto",
    "ok": "OK",
    "revertCancelling": "Deshacer cancelación",
    "reallyRevertCancellationText": "¿Seguro que desea deshacer la cancelación?",
    "revertCancellation": "Deshacer cancelación",
    "dontRevert": "No deshacer",
    "cancelCancellationFeeInvoice": "Anular la factura de la tasa de cancelación, si existe",
    "cancellationRevertedTitle": "Cancelación deshecha",
    "cancellationRevertedText": "La cancelación se ha deshecho correctamente",
    "sendMailText": "¿Desea enviar al cliente un correo de cancelación?"
  }
}
</i18n>
